import { useEffect, useMemo, useState } from 'react'
import { useSalesForecast } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ComposedChart,
  Line,
  Area,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TrendingUp, Loader2, Info, Download } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import {
  CHART_COLORS,
  CHART_AXIS,
  CATEGORICAL,
  GRACKLE_COLORS,
  ROTATED_LABEL_CHART_MARGIN,
} from '@/lib/theme'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import {
  describeSpan,
  fiscalQuarterKeyOf,
  HORIZON_OPTIONS,
  LOOKBACK_OPTIONS,
  periodLabel,
  periodNoun,
  remapPeriodChoice,
  type Grain,
} from '@/lib/fiscal'

const COLORS = CHART_COLORS

interface SalesForecastChartProps {
  months?: number
  forecastPeriod?: number
  category?: string
  owner_id?: string
  /** Hide the in-card category Tabs when the parent owns the category control. */
  showCategoryTabs?: boolean
  /** Display grain. Fiscal quarters (Q1 = Mar–May) when 'quarter'. */
  grain?: Grain
  /** Hide the in-card grain Tabs when the parent owns the grain control. */
  showGrainToggle?: boolean
}

export function SalesForecastChart({
  months = 12,
  forecastPeriod = 3,
  category: externalCategory,
  owner_id,
  showCategoryTabs = true,
  grain: externalGrain,
  showGrainToggle = true,
}: SalesForecastChartProps) {
  const [internalCategory, setInternalCategory] = useState<string>('all')
  const [showYoY, setShowYoY] = useState(false)
  const [internalGrain, setInternalGrain] = useState<Grain>('month')
  const grain = externalGrain ?? internalGrain
  // Interactive lookback (history window) and horizon (forecast length); default to the
  // props so callers can still set the initial scope.
  const [lookback, setLookback] = useState<number>(months)
  const [horizon, setHorizon] = useState<number>(forecastPeriod)

  // Follow the props when the parent changes them. These used to seed state on mount
  // only, so the page's own Historical Months / Forecast Period selects silently stopped
  // agreeing with the in-card ones after the first change.
  useEffect(() => setLookback(months), [months])
  useEffect(() => setHorizon(forecastPeriod), [forecastPeriod])

  const lookbackOptions = LOOKBACK_OPTIONS[grain]
  const horizonOptions = HORIZON_OPTIONS[grain]

  // Switching grain re-labels the two selects, and the option lists differ — quarterly
  // lookbacks start at 12 months. Snap the held value onto the new list, or Radix finds
  // no matching item and renders the placeholder, leaving the control looking empty.
  const setGrain = (next: Grain) => {
    setLookback((v) => remapPeriodChoice(v, LOOKBACK_OPTIONS[next]))
    setHorizon((v) => remapPeriodChoice(v, HORIZON_OPTIONS[next]))
    setInternalGrain(next)
  }

  const activeCategory =
    externalCategory ?? (internalCategory === 'all' ? undefined : internalCategory)
  const { data, isLoading, error } = useSalesForecast({
    months: lookback,
    forecastPeriod: horizon,
    category: activeCategory,
    owner_id,
    grain,
  })

  // Combine historical and forecast data, merging the prior-year (YoY) and
  // weighted-pipeline overlays onto their respective periods.
  //
  // Everything is keyed on `period_key` — 'YYYY-MM' or 'YYYY-QN', whichever the grain —
  // rather than on the formatted display label as it once was. Keying on the label
  // happened to work while labels were unique per row, but it made the join depend on a
  // presentation detail, and in quarterly mode the labels are far coarser.
  const chartData = useMemo(() => {
    if (!data) return []

    const keyOf = (row: { period_key?: string; month?: string }) =>
      row.period_key ?? String(row.month ?? '').slice(0, 7)
    const label = (key: string) => periodLabel(key, grain, true)

    const priorByKey = new Map<string, number | null>(
      [...(data.historicalPriorYear || []), ...(data.forecastPriorYear || [])].map((p) => [
        keyOf(p),
        p.revenue,
      ])
    )
    const pipelineByKey = new Map<string, number>(
      (data.pipelineByMonth || []).map((p) => [keyOf(p), Number(p.weighted_pipeline)])
    )

    type Row = Record<string, unknown> & { periodKey: string; month: string }

    const combined: Row[] = (data.historical || []).map((item) => {
      const key = keyOf(item)
      return {
        periodKey: key,
        month: label(key),
        revenue: Number(item.revenue),
        priorYear: priorByKey.get(key) ?? null,
        type: 'historical',
        dealCount: item.deal_count,
        wonCount: item.won_count,
        monthsInPeriod: item.months_in_period,
        partial: item.partial,
      }
    })
    const indexByKey = new Map(combined.map((r, i) => [r.periodKey, i]))

    let boundaryMerged = false
    for (const item of data.forecast || []) {
      const key = keyOf(item)
      const forecastFields = {
        forecastedRevenue: Number(item.forecasted_revenue),
        lowerBound: Number(item.lower_bound),
        upperBound: Number(item.upper_bound),
        weightedPipeline: pipelineByKey.get(key) ?? null,
        priorYear: priorByKey.get(key) ?? null,
        confidence: item.confidence,
        inProgress: item.in_progress,
        actualToDate: item.actual_to_date,
        projectedRemaining: item.projected_remaining,
        forecastMonthsInPeriod: item.months_in_period,
        forecastPartial: item.partial,
      }
      const existing = indexByKey.get(key)
      if (existing !== undefined) {
        // The same period on BOTH sides — the period in progress. On a quarterly view
        // that is normal: fiscal Q2 has its complete months in `historical` and its
        // remaining month on the forecast side. Merge rather than push, or the category
        // axis grows a second tick for one quarter (the duplicate-"Jun 2026" bug the
        // monthly connector below was written to avoid).
        const base = combined[existing] as Row
        combined[existing] = {
          ...base,
          ...forecastFields,
          periodKey: base.periodKey,
          month: base.month,
          type: 'transition',
        }
        boundaryMerged = true
      } else {
        indexByKey.set(key, combined.length)
        combined.push({ periodKey: key, month: label(key), ...forecastFields, type: 'forecast' })
      }
    }

    // When nothing overlapped — the monthly case, where history stops at the last
    // complete month and the forecast starts at the current one — the dashed line needs
    // somewhere to start. Seed the last historical point with its own value so the two
    // lines meet, and mark it as the boundary for the ReferenceLine.
    const historicalCount = (data.historical || []).length
    if (!boundaryMerged && (data.forecast || []).length > 0 && historicalCount > 0) {
      const lastIdx = historicalCount - 1
      const last = combined[lastIdx] as Row & { revenue: number }
      combined[lastIdx] = {
        ...last,
        forecastedRevenue: last.revenue,
        lowerBound: last.revenue,
        upperBound: last.revenue,
        type: 'transition',
      }
    }

    // Actuals booked so far in the period that is still running, plotted as a distinct
    // point against that period's forecast for a direct "so far vs expected" read.
    //
    // On a quarterly view the current MONTH alone would understate it, because the
    // quarter's earlier complete months are already banked — so the marker shows the
    // whole quarter to date: the complete months plus the month in progress.
    const cm = data.currentMonth
    if (cm) {
      const monthKey = String(cm.month).slice(0, 7)
      const key = grain === 'quarter' ? fiscalQuarterKeyOf(monthKey) : monthKey
      const idx = indexByKey.get(key)
      if (idx !== undefined) {
        const row = combined[idx] as Row & { actualToDate?: number }
        combined[idx] = {
          ...row,
          periodToDate: Number(row.actualToDate ?? 0) + Number(cm.revenue),
        }
      } else {
        combined.push({
          periodKey: key,
          month: label(key),
          periodToDate: Number(cm.revenue),
          type: 'mtd',
        })
      }
    }

    return combined
  }, [data, grain])

  // Flat rows for the accessible table + CSV export (historical then forecast).
  const tableRows = useMemo(() => {
    if (!data) return []
    // Keyed on period_key like the chart, and labelled through the same helper, so the
    // table, the CSV and the chart can never disagree about what a row is called.
    const keyOf = (row: { period_key?: string; month?: string }) =>
      row.period_key ?? String(row.month ?? '').slice(0, 7)
    const fmt = (key: string) => periodLabel(key, grain)

    const priorByKey = new Map(
      [...(data.historicalPriorYear || []), ...(data.forecastPriorYear || [])].map((p) => [
        keyOf(p),
        p.revenue,
      ])
    )
    const pipelineByKey = new Map(
      (data.pipelineByMonth || []).map((p) => [keyOf(p), Number(p.weighted_pipeline)])
    )
    const hist = (data.historical || []).map((h) => ({
      month: fmt(keyOf(h)),
      kind: h.partial ? 'Actual (period in progress)' : 'Actual',
      revenue: Number(h.revenue),
      forecast: null as number | null,
      lower: null as number | null,
      upper: null as number | null,
      pipeline: null as number | null,
      priorYear: priorByKey.get(keyOf(h)) ?? null,
    }))
    const fc = (data.forecast || []).map((f) => ({
      month: fmt(keyOf(f)),
      kind: f.partial
        ? `Forecast (partial — ${f.months_in_period} of 3 months)`
        : f.in_progress
          ? 'Forecast (actual + remaining)'
          : 'Forecast',
      revenue: null as number | null,
      forecast: Number(f.forecasted_revenue),
      lower: Number(f.lower_bound),
      upper: Number(f.upper_bound),
      pipeline: pipelineByKey.get(keyOf(f)) ?? null,
      priorYear: (priorByKey.get(keyOf(f)) ?? null) as number | null,
    }))
    const cm = data.currentMonth
      ? [
          {
            // Always the calendar month, whatever the grain — this row is specifically
            // "what has been booked this month", so labelling it as a quarter would
            // misstate the span it covers.
            month: periodLabel(String(data.currentMonth.month).slice(0, 7), 'month'),
            kind: 'Month-to-date',
            revenue: Number(data.currentMonth.revenue),
            forecast: null as number | null,
            lower: null as number | null,
            upper: null as number | null,
            pipeline: null as number | null,
            priorYear: null as number | null,
          },
        ]
      : []
    return [...hist, ...fc, ...cm]
  }, [data, grain])

  const handleExport = () => {
    exportToCSV({
      filename: `sales-forecast${activeCategory ? `-${activeCategory}` : ''}`,
      data: tableRows,
      columns: ['month', 'kind', 'revenue', 'forecast', 'lower', 'upper', 'pipeline', 'priorYear'],
      headers: [
        grain === 'quarter' ? 'Fiscal Quarter' : 'Month',
        'Type',
        'Actual Revenue (CAD)',
        'Forecast (CAD)',
        'Lower Bound (CAD)',
        'Upper Bound (CAD)',
        'Weighted Pipeline (CAD)',
        'Last Year (CAD)',
      ],
    })
  }

  const metrics = data?.metrics
  // The current partial month's forecast entry is reconciled to actuals-to-date + a prorated
  // remainder (backend). It is not necessarily forecast[0] — gaps in the historical series can
  // push the anchor back so the current month lands later in the array. Find it by flag and
  // headline it; otherwise fall back to the first forecast month.
  // On the quarterly grain the period in progress is the blended one; on the monthly
  // grain it is the reconciled current month. Either way it is the period the reader is
  // actually selling into, so it leads the insights panel.
  const reconciledForecast = data?.forecast?.find((f) => f.reconciled || f.in_progress)
  const primaryForecast = reconciledForecast ?? data?.forecast?.[0]

  // How much of the window the year-on-year figure actually covers. The backend counts
  // whole periods, so on the quarterly grain this is quarters and part-quarters are
  // excluded — which is why it can sit well below the number of bars on screen.
  const comparedPeriods = data?.yoy_periods_compared ?? data?.yoy_months_compared ?? null
  // The "booked so far + expected" breakdown under the headline, derived so the two parts
  // ALWAYS sum to the headline they sit beneath.
  //
  // It previously read both figures off `currentMonth`, which is a MONTH measure — correct
  // on the monthly grain, but under a quarterly headline it printed "CA$0.00 booked so far
  // + CA$281,838 expected" beneath a CA$1,691,732 total, because the quarter's already-
  // complete months were nowhere in the sum.
  //
  // Booked = the quarter's complete months (`actual_to_date`, absent on the monthly grain)
  // plus whatever the month in progress has banked — the same quantity the chart's
  // "so far" marker plots. Expected is then the remainder by subtraction, so the arithmetic
  // cannot drift from the total again.
  const bookedSoFar =
    (reconciledForecast?.actual_to_date ?? 0) + Number(data?.currentMonth?.revenue ?? 0)
  const expectedRest = Math.max(0, (primaryForecast?.forecasted_revenue ?? 0) - bookedSoFar)

  // How many MONTHLY points actually fed the model. `historical.length` is quarters on the
  // quarterly grain, so quoting it beside the words "complete months" was wrong twice over —
  // wrong unit and wrong count (it read "13 complete months" for 13 quarters of history).
  const modelMonths =
    grain === 'quarter'
      ? (data?.historical ?? []).reduce((a, r) => a + (r.months_in_period ?? 3), 0)
      : (data?.historical ?? []).length

  const partialForecastPeriods = (data?.forecast ?? []).filter(
    (f) => f.partial && f.months_in_period != null
  ) as Array<{ period_key: string; months_in_period: number }>
  const spanPeriods = grain === 'quarter' ? Math.round(Number(lookback) / 3) : Number(lookback)

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sales Forecast</CardTitle>
          <CardDescription>Forecasted revenue with confidence intervals</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">Failed to load sales forecast data. Please try again.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
              Sales Forecast
            </CardTitle>
            <CardDescription>
              {describeSpan(lookback, grain)} of history with a {describeSpan(horizon, grain)}{' '}
              forecast
              {activeCategory
                ? ` — ${activeCategory}${activeCategory === 'Services' ? ' (includes uncategorized)' : ''}`
                : ''}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {showCategoryTabs && !externalCategory && (
              <Tabs value={internalCategory} onValueChange={setInternalCategory}>
                <TabsList aria-label="Product category">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="Software">Software</TabsTrigger>
                  <TabsTrigger value="Services">Services</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            {/* Deliberately in the header, not in the controls row below: that row lives
                inside the empty-state branch and disappears when a query returns nothing,
                which would strand the reader in a grain they cannot switch out of. */}
            {showGrainToggle && !externalGrain && (
              <Tabs value={grain} onValueChange={(v) => setGrain(v as Grain)}>
                <TabsList aria-label="Time grain">
                  <TabsTrigger value="month">Monthly</TabsTrigger>
                  <TabsTrigger value="quarter">Quarterly</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
          {metrics && (
            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-2 mb-1">
                <Info className="h-4 w-4" aria-hidden="true" />
                <span className="font-medium">Trend: {metrics.trend}</span>
              </div>
              <div className="text-xs space-y-0.5">
                <div>SMA3: {formatCurrency(metrics.sma3, 'CAD')}</div>
                <div>SMA6: {formatCurrency(metrics.sma6, 'CAD')}</div>
                <div>SMA12: {formatCurrency(metrics.sma12, 'CAD')}</div>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-[400px]">
            <Loader2
              className="h-8 w-8 animate-spin text-muted-foreground"
              aria-label="Loading sales forecast data"
            />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground space-y-2">
            <p className="text-lg font-medium">No forecast data available</p>
            <p className="text-sm text-center max-w-md">
              Sales forecasting requires closed opportunities with recent close dates (within the
              last 12 months). Your data may be from an older time period.
            </p>
            {data?.historical && data.historical.length > 0 && (
              <p className="text-xs mt-2">
                Found {data.historical.length} months of historical data, but may be outside the
                default lookback window.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <label htmlFor="forecast-lookback" className="text-xs text-muted-foreground">
                    Lookback
                  </label>
                  <Select value={String(lookback)} onValueChange={(v) => setLookback(Number(v))}>
                    <SelectTrigger
                      id="forecast-lookback"
                      className="h-8 w-[92px]"
                      aria-label={`History lookback window in ${periodNoun(grain)}s`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {lookbackOptions.map((o) => (
                        <SelectItem key={o.months} value={String(o.months)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1.5">
                  <label htmlFor="forecast-horizon" className="text-xs text-muted-foreground">
                    Horizon
                  </label>
                  <Select value={String(horizon)} onValueChange={(v) => setHorizon(Number(v))}>
                    <SelectTrigger
                      id="forecast-horizon"
                      className="h-8 w-[92px]"
                      aria-label={`Forecast horizon in ${periodNoun(grain)}s`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {horizonOptions.map((o) => (
                        <SelectItem key={o.months} value={String(o.months)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={showYoY ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowYoY((v) => !v)}
                  aria-pressed={showYoY}
                  aria-label="Toggle last-year comparison overlay"
                >
                  Compare last year
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  aria-label="Export forecast data as CSV"
                >
                  <Download className="h-4 w-4 mr-1" aria-hidden="true" />
                  Export CSV
                </Button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={chartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                <defs>
                  <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[2]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS[2]} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  content={<ChartTooltipContent />}
                  formatter={(value: any, name: string) => {
                    if (typeof value === 'number') {
                      return [formatCurrency(value, 'CAD'), name]
                    }
                    return [value, name]
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />

                {/* Confidence interval area (forecast only) */}
                {/* tooltipType="none": the band's edges already appear as the Upper/Lower
                    Bound rows, so its own row would only duplicate the Upper Bound value. */}
                <Area
                  type="monotone"
                  dataKey="upperBound"
                  stroke="none"
                  fill="url(#colorConfidence)"
                  fillOpacity={1}
                  name="Confidence Range"
                  tooltipType="none"
                  connectNulls
                />
                {/* Background-colored mask that cuts the band's underside — a rendering
                    helper, so keep it out of the tooltip and legend entirely. */}
                <Area
                  type="monotone"
                  dataKey="lowerBound"
                  stroke="none"
                  fill="hsl(var(--background))"
                  fillOpacity={1}
                  legendType="none"
                  tooltipType="none"
                  connectNulls
                />

                {/* Historical revenue line */}
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={COLORS[0]}
                  strokeWidth={3}
                  dot={{ fill: COLORS[0], r: 4 }}
                  name="Historical Revenue"
                  connectNulls={false}
                />

                {/* Forecasted revenue line */}
                <Line
                  type="monotone"
                  dataKey="forecastedRevenue"
                  stroke={COLORS[1]}
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={{ fill: COLORS[1], r: 4 }}
                  name="Forecasted Revenue"
                  connectNulls
                />

                {/* Pipeline-implied projection: weighted open pipeline expected to close
                    in the forecast months — an alternative to the statistical forecast. */}
                <Line
                  type="monotone"
                  dataKey="weightedPipeline"
                  stroke={COLORS[4]}
                  strokeWidth={2}
                  strokeDasharray="2 3"
                  dot={{ fill: COLORS[4], r: 3 }}
                  name="Pipeline-implied"
                  connectNulls
                />

                {/* Actuals booked so far in the period still running — a single distinct
                    marker so progress can be read against that period's forecast point.
                    On the quarterly grain this covers the whole quarter to date, since
                    its earlier complete months are already banked. */}
                <Scatter
                  dataKey="periodToDate"
                  fill={CATEGORICAL.pollen.solid}
                  stroke={CATEGORICAL.prussian.solid}
                  strokeWidth={1}
                  name={`This ${periodNoun(grain)} so far`}
                />

                {/* Prior-year revenue (YoY) — historical + forecast months, toggled */}
                {showYoY && (
                  <Line
                    type="monotone"
                    dataKey="priorYear"
                    stroke={COLORS[3]}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    name="Last Year"
                    connectNulls
                  />
                )}

                {/* Upper bound line — evergreen (ceiling). Distinct from the Lower Bound and
                    from the gold Confidence Range fill / MTD marker so the band is readable. */}
                <Line
                  type="monotone"
                  dataKey="upperBound"
                  stroke={CATEGORICAL.evergreen.solid}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  dot={false}
                  name="Upper Bound"
                  connectNulls
                />

                {/* Lower bound line — clay (floor). */}
                <Line
                  type="monotone"
                  dataKey="lowerBound"
                  stroke={CATEGORICAL.clay.solid}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  dot={false}
                  name="Lower Bound"
                  connectNulls
                />

                {/* Reference line to separate historical from forecast */}
                <ReferenceLine
                  x={
                    (chartData as Array<{ type?: string; month?: string }>).find(
                      (d) => d.type === 'transition'
                    )?.month
                  }
                  stroke={CHART_AXIS}
                  strokeDasharray="3 3"
                  // INSIDE the plot area, not 'top'. Positioned above it, the label sat in
                  // the 8px top margin and was sliced in half — it read as a smear of grey
                  // over the gridlines. Anchored LEFT rather than right (as the equivalent
                  // in RevenueTrendReport is) because this line sits at the actual/forecast
                  // boundary: measured across every lookback/horizon pair, the room to its
                  // right falls as low as 7.7% of the width, against 29.4% to its left.
                  label={{
                    value: 'Forecast →',
                    position: 'insideTopLeft',
                    fontSize: 11,
                    fill: CHART_AXIS,
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* A short bar is drawn the same width as a full quarter, so it reads as a
                decline unless it says otherwise. Named here rather than left to be
                inferred from the shape of the line. */}
            {grain === 'quarter' && partialForecastPeriods.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {partialForecastPeriods
                  .map(
                    (p) =>
                      `${periodLabel(p.period_key, 'quarter')} covers only ${p.months_in_period} of its 3 months`
                  )
                  .join('; ')}{' '}
                — the forecast horizon ends mid-quarter, so that bar is short rather than the
                forecast falling.
              </p>
            )}

            {/* Forecast insights */}
            {data?.forecast && data.forecast.length > 0 && (
              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4" aria-hidden="true" />
                  Forecast Insights
                </h4>
                <div className="grid gap-3 md:grid-cols-2 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">
                      {reconciledForecast
                        ? `This ${periodNoun(grain)} (projected total)`
                        : `Next ${periodNoun(grain)} forecast`}
                    </p>
                    <p className="font-semibold text-lg">
                      {formatCurrency(primaryForecast?.forecasted_revenue ?? 0, 'CAD')}
                    </p>
                    {reconciledForecast && data.currentMonth ? (
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(bookedSoFar, 'CAD')} booked so far +{' '}
                        {formatCurrency(expectedRest, 'CAD')} expected over the rest of the{' '}
                        {periodNoun(grain)}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {primaryForecast?.confidence ?? 0}% confidence
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-muted-foreground mb-1">How this projection is calculated</p>
                    {metrics?.base != null && metrics.weights ? (
                      <div className="text-xs space-y-1">
                        <p>
                          Base{' '}
                          <span className="font-semibold">
                            {formatCurrency(metrics.base, 'CAD')}
                          </span>{' '}
                          ={' '}
                          {(['sma3', 'sma6', 'sma12'] as const)
                            .filter((k) => metrics.weights?.[k] != null)
                            .map(
                              (k) =>
                                `${Math.round((metrics.weights?.[k] ?? 0) * 100)}%·${k.toUpperCase()} (${formatCurrency(
                                  metrics[k],
                                  'CAD'
                                )})`
                            )
                            .join(' + ')}
                        </p>
                        <p>
                          Trend{' '}
                          <span className="font-semibold">
                            {metrics.trendValue >= 0 ? '+' : ''}
                            {formatCurrency(metrics.trendValue, 'CAD')}/mo
                          </span>{' '}
                          (linear regression) → next month ≈ base + trend ={' '}
                          <span className="font-semibold">
                            {formatCurrency((metrics.base ?? 0) + (metrics.trendValue ?? 0), 'CAD')}
                          </span>
                          . Confidence band widens ±15% to ±25% over the horizon.
                        </p>
                        <p className="text-muted-foreground">
                          Base uses {modelMonths} complete months of Closed-Won revenue (the current
                          partial month and open pipeline are excluded from the SMA/trend). The
                          model is monthly on both views — a quarter is the sum of its months, so
                          the averages above are monthly run-rates. The {periodNoun(grain)} in
                          progress is shown as actuals-to-date + a prorated remainder, so it won't
                          equal the raw base.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs">
                        Weighted Simple Moving Average (SMA) with linear regression trend analysis.
                        Confidence intervals based on forecast horizon (±15% to ±25%).
                      </p>
                    )}
                  </div>
                  {metrics?.trend && (
                    <div>
                      <p className="text-muted-foreground mb-1">Market Trend</p>
                      <p className="font-medium capitalize flex items-center gap-1">
                        {metrics.trend === 'increasing' && (
                          <TrendingUp
                            className="h-4 w-4"
                            style={{ color: GRACKLE_COLORS.navy }}
                            aria-hidden="true"
                          />
                        )}
                        {metrics.trend === 'decreasing' && (
                          <TrendingUp
                            className="h-4 w-4 rotate-180"
                            style={{ color: GRACKLE_COLORS.darkRaspberry }}
                            aria-hidden="true"
                          />
                        )}
                        {metrics.trend}
                      </p>
                      {metrics.trendValue && (
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(Math.abs(metrics.trendValue), 'CAD')} per month
                        </p>
                      )}
                    </div>
                  )}
                  {data.pipeline && data.pipeline.length > 0 && (
                    <div>
                      <p className="text-muted-foreground mb-1">
                        Pipeline Value (next {describeSpan(horizon, grain)})
                      </p>
                      <p className="font-semibold text-lg">
                        {/*
                          'CAD' is required, not decorative. formatCurrency defaults to
                          USD, so this rendered CAD figures as "$…" while every
                          neighbouring number on the page showed "CA$…".
                        */}
                        {formatCurrency(
                          data.pipeline.reduce(
                            (sum: number, item: any) => sum + Number(item.weighted_value),
                            0
                          ),
                          'CAD'
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {data.pipeline.reduce(
                          (sum: number, item: any) => sum + Number(item.opportunity_count),
                          0
                        )}{' '}
                        opportunities
                      </p>
                    </div>
                  )}
                  {data.coverage_ratio != null && (
                    <div>
                      <p className="text-muted-foreground mb-1">Pipeline Coverage</p>
                      <p className="font-semibold text-lg">{data.coverage_ratio}× forecast</p>
                      <p className="text-xs text-muted-foreground">
                        Weighted open pipeline ÷ forecasted revenue (next{' '}
                        {describeSpan(horizon, grain)})
                      </p>
                    </div>
                  )}
                  {data.yoy_change_pct != null && (
                    <div>
                      <p className="text-muted-foreground mb-1">Year over Year</p>
                      <p
                        className="font-semibold text-lg"
                        style={{
                          color:
                            data.yoy_change_pct >= 0
                              ? GRACKLE_COLORS.navy
                              : GRACKLE_COLORS.darkRaspberry,
                        }}
                      >
                        {data.yoy_change_pct >= 0 ? '+' : ''}
                        {data.yoy_change_pct}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {/*
                          `lookback`, not the `months` prop: the data was fetched with
                          the lookback state, so changing the in-card Lookback select
                          refetched the numbers while this caption kept the old figure.

                          And the count is the months actually compared, which is fewer
                          than the lookback whenever the business has no year-ago
                          trading for part of the window. Saying "the same 24 months"
                          over a 17-month comparison is the claim this card used to make.
                        */}
                        vs the same{' '}
                        {describeSpan(
                          comparedPeriods != null && comparedPeriods < spanPeriods
                            ? comparedPeriods * (grain === 'quarter' ? 3 : 1)
                            : lookback,
                          grain
                        )}{' '}
                        last year
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Accessible companion table (same data as the chart) + export. */}
            {tableRows.length > 0 && (
              <details className="mt-4 rounded-md border bg-card">
                <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
                  Forecast data table (accessible)
                </summary>
                <div className="px-4 pb-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <caption className="sr-only">
                      Monthly actual revenue, forecast, confidence bounds, weighted pipeline, and
                      prior-year revenue
                    </caption>
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th scope="col" className="py-1">
                          Month
                        </th>
                        <th scope="col" className="py-1">
                          Type
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Actual
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Forecast
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Lower
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Upper
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Pipeline
                        </th>
                        <th scope="col" className="py-1 text-right">
                          Last Year
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((r, i) => (
                        <tr key={`${r.month}-${r.kind}-${i}`} className="border-t">
                          <td className="py-1">{r.month}</td>
                          <td className="py-1">{r.kind}</td>
                          <td className="py-1 text-right">
                            {r.revenue != null ? formatCurrency(r.revenue, 'CAD') : '—'}
                          </td>
                          <td className="py-1 text-right">
                            {r.forecast != null ? formatCurrency(r.forecast, 'CAD') : '—'}
                          </td>
                          <td className="py-1 text-right">
                            {r.lower != null ? formatCurrency(r.lower, 'CAD') : '—'}
                          </td>
                          <td className="py-1 text-right">
                            {r.upper != null ? formatCurrency(r.upper, 'CAD') : '—'}
                          </td>
                          <td className="py-1 text-right">
                            {r.pipeline != null ? formatCurrency(r.pipeline, 'CAD') : '—'}
                          </td>
                          <td className="py-1 text-right">
                            {r.priorYear != null ? formatCurrency(r.priorYear, 'CAD') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
